import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, Select, Switch, Alert, toast } from '../../components/common';
import { userApi, departmentApi, roleApi } from '../../services/api';
import './UserForm.css';

export default function UserForm({ user, onSuccess, onCancel }) {
    const isEditing = !!user;

    const [form, setForm] = useState({
        email: '',
        password: '',
        fullName: '',
        phone: '',
        roleId: '',
        departmentId: '',
        isActive: true
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (user) {
            setForm({
                email: user.email || '',
                password: '',
                fullName: user.fullName || '',
                phone: user.phone || '',
                roleId: user.roleId || user.role?.id || '',
                departmentId: user.departmentId || user.department?.id || '',
                isActive: user.isActive ?? true
            });
        }
    }, [user]);

    // Fetch roles from API
    const { data: roles = [] } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            try {
                const res = await roleApi.list();
                return res.data?.data || res.data || [];
            } catch {
                return [];
            }
        }
    });

    // Fetch departments from API
    const { data: departments = [] } = useQuery({
        queryKey: ['departments'],
        queryFn: async () => {
            try {
                const res = await departmentApi.list();
                return res.data?.data || res.data || [];
            } catch {
                return [];
            }
        }
    });

    // Create/Update mutation
    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? userApi.update(user.id, data)
            : userApi.create(data),
        onSuccess: () => {
            toast.success(`User ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            const message = err.response?.data?.error?.message || 'Failed to save user';
            toast.error(message);

            // Handle validation errors
            if (err.response?.data?.error?.details) {
                const fieldErrors = {};
                err.response.data.error.details.forEach(detail => {
                    fieldErrors[detail.field] = detail.message;
                });
                setErrors(fieldErrors);
            }
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};

        if (!form.email) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email format';

        if (!isEditing && !form.password) newErrors.password = 'Password is required';
        else if (form.password && form.password.length < 8) newErrors.password = 'Password must be at least 8 characters';

        if (!form.fullName) newErrors.fullName = 'Full name is required';
        if (!form.roleId) newErrors.roleId = 'Role is required';

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validate()) return;

        const payload = {
            email: form.email,
            fullName: form.fullName,
            phone: form.phone || null,
            roleId: form.roleId, // Keep as UUID string
            departmentId: form.departmentId || null, // Keep as UUID string or null
            isActive: form.isActive
        };

        if (form.password) {
            payload.password = form.password;
        }

        mutation.mutate(payload);
    };

    const roleOptions = roles.map(r => ({
        value: r.id,
        label: r.name
    }));

    const deptOptions = [
        { value: '', label: 'No Department' },
        ...departments.map(d => ({
            value: d.id,
            label: d.name
        }))
    ];

    return (
        <form onSubmit={handleSubmit} className="user-form">
            <div className="form-grid">
                <Input
                    name="fullName"
                    label="Full Name"
                    placeholder="Enter full name"
                    value={form.fullName}
                    onChange={handleChange}
                    error={errors.fullName}
                    required
                />

                <Input
                    name="email"
                    type="email"
                    label="Email"
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                    disabled={isEditing}
                />

                <Input
                    name="password"
                    type="password"
                    label={isEditing ? 'New Password (leave blank to keep current)' : 'Password'}
                    placeholder={isEditing ? 'Enter new password' : 'Enter password'}
                    value={form.password}
                    onChange={handleChange}
                    error={errors.password}
                    required={!isEditing}
                />

                <Input
                    name="phone"
                    label="Phone"
                    placeholder="Enter phone number"
                    value={form.phone}
                    onChange={handleChange}
                    error={errors.phone}
                />

                <Select
                    name="roleId"
                    label="Role"
                    placeholder="Select role"
                    value={form.roleId}
                    onChange={handleChange}
                    options={roleOptions}
                    error={errors.roleId}
                    required
                />

                <Select
                    name="departmentId"
                    label="Department"
                    placeholder="Select department"
                    value={form.departmentId}
                    onChange={handleChange}
                    options={deptOptions}
                    error={errors.departmentId}
                />
            </div>

            <div className="form-switch">
                <Switch
                    label="Active"
                    checked={form.isActive}
                    onChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update User' : 'Create User'}
                </Button>
            </div>
        </form>
    );
}

UserForm.propTypes = {
    user: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
